import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {
  ClientPatientSearchDialogComponent,
  ClientPatientSearchData,
  ClientPatientSearchResult
} from './client-patient-search.dialog';
import {Client} from 'src/app/client-management/models/client.interface';
import {Patient} from 'src/app/patient-management/models/patient.interface';

@Injectable({
  providedIn: 'root'
})
export class ClientPatientSearchService {

  constructor(private dialog: MatDialog) {
  }

  /**
   * Opens a dialog for searching and selecting a client and patient
   * @param data Initial data to populate the dialog with
   * @param dialogWidth Width of the dialog (default: '600px')
   * @returns An Observable that resolves to the selected client and patient when the dialog is closed
   */
  openClientPatientSearch(
    data: ClientPatientSearchData = {},
    dialogWidth: string = '600px'
  ): Observable<ClientPatientSearchResult> {
    const dialogRef = this.dialog.open<
      ClientPatientSearchDialogComponent,
      ClientPatientSearchData,
      ClientPatientSearchResult
    >(
      ClientPatientSearchDialogComponent, {
        width: dialogWidth,
        data,
        disableClose: false
      });

    return dialogRef.afterClosed();
  }

  /**
   * Helper method to quickly open the search dialog with a title
   * @param title Custom title for the dialog
   * @returns An Observable that resolves to the selected client and patient
   */
  quickSearch(title?: string): Observable<ClientPatientSearchResult> {
    return this.openClientPatientSearch({title});
  }

  /**
   * Opens the dialog with a pre-selected client to choose a patient
   * @param client The pre-selected client
   * @param title Custom title for the dialog
   * @returns An Observable that resolves to the selected client and patient
   */
  searchPatientsForClient(client: Client, title?: string): Observable<ClientPatientSearchResult> {
    return this.openClientPatientSearch({
      selectedClient: client,
      title: title || `Select Patient for ${client.name}`
    });
  }

  /**
   * Opens the dialog with pre-selected client and patient for confirmation or changing
   * @param client The pre-selected client
   * @param patient The pre-selected patient
   * @param title Custom title for the dialog
   * @returns An Observable that resolves to the selected client and patient
   */
  confirmOrChangeSelection(
    client: Client,
    patient: Patient,
    title?: string
  ): Observable<ClientPatientSearchResult> {
    return this.openClientPatientSearch({
      selectedClient: client,
      selectedPatient: patient,
      title: title || 'Confirm or Change Selection'
    });
  }
}
